/**
 * Riva — Timed discount popup
 *
 * Frequency is stored, not the timer, so navigating between pages does not
 * reset a visitor's countdown into an endless "45 seconds from now" loop —
 * the elapsed time carries across pages within a visit.
 *
 * Storage is wrapped because Safari private mode throws on localStorage rather
 * than returning null; a storage failure should mean "show the popup", not
 * "break the page".
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function store() {
    try {
      var t = '__riva_t__';
      window.localStorage.setItem(t, '1');
      window.localStorage.removeItem(t);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function sessionStore() {
    try {
      var t = '__riva_s__';
      window.sessionStorage.setItem(t, '1');
      window.sessionStorage.removeItem(t);
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  }

  /**
   * Email signup.
   *
   * The response never carries the discount code — it only reports that the
   * request was accepted — so there is nothing here worth intercepting. The
   * code itself arrives by email.
   *
   * The endpoint answers 200 for a throttled or honeypotted request as well as
   * a genuine one, so this shows the same confirmation either way. That is
   * deliberate: it keeps the page from telling anyone whether an address is
   * already on the list.
   */
  function wireForm(el, onSuccess) {
    var form = el.querySelector('[data-riva-popup-form]');
    if (!form) return;

    var endpoint = form.dataset.endpoint;
    var input = form.querySelector('input[name="email"]');
    var button = form.querySelector('[data-riva-popup-submit]');
    var label = form.querySelector('[data-riva-popup-submit-label]');
    var errorEl = form.querySelector('[data-riva-popup-error]');
    var successEl = el.querySelector('[data-riva-popup-success]');
    var busy = false;

    function fail(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy || !endpoint) return;

      if (errorEl) errorEl.hidden = true;

      var email = (input && input.value || '').trim();
      // Cheap client-side shape check. The server validates properly.
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        fail('Enter a valid email address.');
        if (input) input.focus();
        return;
      }

      var hp = form.querySelector('input[name="hp_zx9"]');
      busy = true;
      if (button) button.disabled = true;
      var originalLabel = label ? label.textContent : '';
      if (label) label.textContent = 'Sending…';

      fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email, hp_zx9: hp ? hp.value : '' }),
      })
        .then(function (res) {
          return res.json().catch(function () { return { ok: res.ok }; });
        })
        .then(function (data) {
          if (!data || data.ok !== true) {
            fail(
              data && data.error === 'invalid_email'
                ? 'Enter a valid email address.'
                : 'Something went wrong. Try again in a moment.'
            );
            return;
          }
          form.hidden = true;
          if (successEl) successEl.hidden = false;
          // Do not nag someone who just signed up.
          if (typeof onSuccess === 'function') onSuccess();
        })
        .catch(function () {
          fail('Could not reach us just now. Try again in a moment.');
        })
        .then(function () {
          busy = false;
          if (button) button.disabled = false;
          if (label) label.textContent = originalLabel;
        });
    });
  }

  function init(el) {
    if (el.dataset.rivaPopupReady === 'true') return;
    el.dataset.rivaPopupReady = 'true';

    var delayMs = (parseInt(el.dataset.delay, 10) || 45) * 1000;
    var frequency = el.dataset.frequency || '7';
    var key = el.dataset.storageKey || 'riva-popup-1';
    var elapsedKey = key + ':elapsed';

    var ls = store();
    var ss = sessionStore();
    var dialog = el.querySelector('.riva-popup__dialog');
    var timer = null;
    var lastFocused = null;

    // In the theme editor we always want to see it, immediately.
    var inEditor = !!(window.Shopify && window.Shopify.designMode);

    function dismissedRecently() {
      if (inEditor) return false;
      if (frequency === 'session') {
        return !!(ss && ss.getItem(key) === 'dismissed');
      }
      if (!ls) return false;
      var raw = ls.getItem(key);
      if (!raw) return false;
      if (frequency === 'never') return true;
      var days = parseInt(frequency, 10);
      if (!days) return false;
      var when = parseInt(raw, 10);
      if (!when) return false;
      return (Date.now() - when) < days * 86400000;
    }

    function remember() {
      if (inEditor) return;
      if (frequency === 'session') {
        if (ss) ss.setItem(key, 'dismissed');
        return;
      }
      if (ls) ls.setItem(key, String(Date.now()));
    }

    function elapsedSoFar() {
      if (!ss) return 0;
      return parseInt(ss.getItem(elapsedKey), 10) || 0;
    }

    function recordElapsed(ms) {
      if (ss) ss.setItem(elapsedKey, String(ms));
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab' || !dialog) return;
      // Keep focus inside the dialog while it is open.
      var items = Array.prototype.filter.call(
        dialog.querySelectorAll(FOCUSABLE),
        function (n) { return n.offsetParent !== null; }
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      if (!el.hidden) return;
      lastFocused = document.activeElement;
      el.hidden = false;
      // Next frame so the transition actually runs.
      requestAnimationFrame(function () { el.classList.add('is-open'); });
      document.addEventListener('keydown', onKeydown);
      var target = dialog && dialog.querySelector(FOCUSABLE);
      if (target) target.focus({ preventScroll: true });
    }

    function close() {
      if (el.hidden) return;
      el.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown);
      remember();
      var finish = function () {
        el.hidden = true;
        if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
      };
      // Wait out the fade, but never hang if transitions are off.
      var done = false;
      el.addEventListener('transitionend', function handler() {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', handler);
        finish();
      });
      setTimeout(function () { if (!done) { done = true; finish(); } }, 400);
    }

    el.querySelectorAll('[data-riva-popup-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    wireForm(el, remember);

    if (dismissedRecently()) return;

    if (inEditor) {
      open();
      return;
    }

    var already = elapsedSoFar();
    var remaining = Math.max(0, delayMs - already);
    var startedAt = Date.now();

    timer = setTimeout(open, remaining);

    // Carry elapsed time to the next page so the delay means time on site,
    // not time on this particular page.
    window.addEventListener('pagehide', function () {
      if (el.hidden) recordElapsed(already + (Date.now() - startedAt));
      if (timer) clearTimeout(timer);
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-riva-popup]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
